// run-command-signal-cleanup.ts
//
// Phase 06g R2c (dogfood 6 F-34): what `avatar autopilot run` does to its
// own on-disk state when a signal stops it. Split out of
// run-command-cli-wiring.ts (195/200 lines).
//   - `forceCleanup` (sync, second signal / SIGHUP re-raise): drop the machine
//     lock + journal lease only when they record THIS pid.
//   - `afterWorkersStopped` (async, awaited by the handler once the worker
//     group is reaped, before `drain()` resolves): clear `active-worker.json`
//     and the binding file — otherwise the next `run` sees a lease still in
//     force plus a record with a dead pid and waits out the lease (~61 min
//     measured), or `doctor` keeps naming a worker that no longer exists.
//   - `waitForRunToSettle` (SIGHUP only): measured in the 06g e2e — the
//     re-raise used to land between `attempt_finished` and the post-route
//     reset, leaving the worker's files for the next run's `worktree_dirty`
//     stop; the loop exits on its own once the worker is gone, so the
//     hang-up exit waits for that (bounded in the handler).
import { clearActiveWorkerSync } from "./active-worker-record.js";
import { forceCleanupOrphanLocksOwnedBySelf } from "./force-cleanup-orphan-locks-owned-by-self.js";
import { cleanupStaleOrphanedBinding } from "./orphaned-ticket-recovery-applier.js";

export interface RunSignalCleanupInput {
  machineLockPath: string;
  controlRoot: string;
  missionWorkspace: string;
}

export interface RunSignalCleanup {
  forceCleanup: () => void;
  afterWorkersStopped: () => Promise<void>;
  /** SIGHUP re-raise waits (bounded) for the run flow — the loop's post-route archive + reset — to finish. */
  waitForRunToSettle: () => Promise<void>;
  /** Called by the wiring once `runOnce` settled (success or throw). */
  markRunSettled: () => void;
}

export function buildRunSignalCleanup(input: RunSignalCleanupInput): RunSignalCleanup {
  let settle: () => void = () => {};
  const settled = new Promise<void>((resolve) => {
    settle = resolve;
  });
  return {
    forceCleanup: () =>
      forceCleanupOrphanLocksOwnedBySelf(input.machineLockPath, input.controlRoot),
    afterWorkersStopped: async () => {
      clearActiveWorkerSync(input.controlRoot);
      await cleanupStaleOrphanedBinding(input.missionWorkspace);
    },
    waitForRunToSettle: () => settled,
    markRunSettled: () => settle(),
  };
}
